import { useRef } from 'react';
import { useReduxState } from 'hooks';
import { subtitleLangCodes } from 'lib/lang';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, Select } from 'components/common/form';
import { FileUploader } from 'components/common/uploader';

import type { BlockVideo } from 'shared-types';

type Subtitles = BlockVideo['locales'][string]['subtitles'];

interface Props {
  id: string;
  onSubmit: ({ subtitleLanguage, subtitleUrl }: Subtitles[number]) => void;
}

const EditorBlockVideoSubtitlesAdd = ({ id, onSubmit }: Props) => {
  const { language, messages, theme } = useReduxState();

  const uploader = useRef<any>(undefined);

  const handleSubmit = async (values) => {
    if (!uploader.current?.state.file) return;

    const result = await uploader.current.upload();
    if (!result) return false;

    onSubmit({
      subtitleLanguage: values.subtitleLanguage,
      subtitleUrl: result.url
    });
  };

  const setUploader = (fileUploader: any) => (uploader.current = fileUploader);

  return (
    <div className="add">
      <ModalButtonWithForm
        buttonCategory="light"
        buttonIcon="plus"
        buttonSize="small"
        buttonText={messages.videoCaptionsAdd}
        modalActionCategory="success"
        modalActionText={messages.commonAdd}
        modalTitle={messages.videoCaptionsAdd}
        onSubmit={handleSubmit}
      >
        {() => (
          <>
            <FormGroup label={`${messages.commonFile} (.vtt)`}>
              <div className="uploader">
                <FileUploader
                  allowedFileTypes={['.vtt']}
                  buttonDarkStyle={true}
                  getUploader={setUploader}
                  id={`video-subtitles-uploader-${id}`}
                  keyPrefix="uploads/files/"
                  placeholder={messages.uploaderChooseFile}
                  uuidAsFilename={true}
                />
              </div>
            </FormGroup>

            <FormGroup label={messages.commonLanguage}>
              <div className="language">
                <Select
                  field="subtitleLanguage"
                  initialValue={
                    {
                      'de-DE': 'de',
                      'en-GB': 'en',
                      'es-ES': 'es',
                      'et-EE': 'et',
                      'fi-FI': 'fi',
                      'fr-FR': 'fr',
                      'nb-NO': 'no',
                      'pl-PL': 'pl',
                      'ru-RU': 'ru',
                      'sv-SE': 'sv'
                    }[language]
                  }
                  isSearchable={true}
                  label={messages.editorBlockVideoSubtitleLanguage}
                  options={Object.keys(subtitleLangCodes).map((lang) => ({
                    label: subtitleLangCodes[lang],
                    value: lang
                  }))}
                />
              </div>
            </FormGroup>
          </>
        )}
      </ModalButtonWithForm>

      <style jsx>{`
        .file-language {
          background-color: white;
          border-radius: 5px;
          color: ${theme.textColorLighter};
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 700;
          margin-right: 15px;
          padding: 6px 14px;
          width: 180px;
        }
        .file-language-icon {
          display: inline-block;
          font-size: 1.5rem;
          margin-right: 7px;
        }
        .file-language-text {
          display: inline-block;
          font-size: 1.4rem;
          position: relative;
          top: -1px;
        }
        .file-delete {
          display: inline-block;
        }
        .uploader {
          display: inline-block;
        }
        .language {
          max-width: 200px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockVideoSubtitlesAdd;
